import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormHelperText,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useCreateBeat,
  useCreateSession,
  useDeleteBeat,
  useMapBeatRange,
  useReorderBeat,
  useStoryArchitectSession,
  useStoryStructures,
  useUpdateBeat,
  type BeatDto,
} from '../../api/queries/storyArchitect';
import { useBook, useChapters, useParagraphs } from '../../api/queries/books';
import { useStoryStudioSession } from '../../api/queries/storyStudio';
import { GenerationModal } from '../ai/GenerationModal';
import { BeatTimeline } from './BeatTimeline';

type ViewMode = 'list' | 'timeline';

interface Props {
  bookId: string;
}

/**
 * Horizontal beat timeline above the editor. Collapsible via expand/collapse. Clicking
 * a beat opens a side drawer with details + paragraph-range picker.
 */
export function StoryArchitectPanel({ bookId }: Props) {
  const { data: session, isLoading } = useStoryArchitectSession(bookId);
  const { data: structures = [] } = useStoryStructures();
  const { data: book } = useBook(bookId);
  const { data: storySession } = useStoryStudioSession(bookId);
  const createSession = useCreateSession(bookId);
  const createBeat = useCreateBeat(bookId);
  const reorderBeat = useReorderBeat(bookId);
  const [expanded, setExpanded] = useState(true);
  const [selectedBeat, setSelectedBeat] = useState<BeatDto | null>(null);
  const [createSessionOpen, setCreateSessionOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [suggestNextOpen, setSuggestNextOpen] = useState(false);
  const [generateFromSynopsisOpen, setGenerateFromSynopsisOpen] =
    useState(false);
  const [addBeatDialogOpen, setAddBeatDialogOpen] = useState(false);
  const [addBeatName, setAddBeatName] = useState('');

  const beats = useMemo(() => session?.beats ?? [], [session]);
  const ids = useMemo(() => beats.map((b) => b.id), [beats]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(beats, oldIndex, newIndex);
    void reorderBeat.mutateAsync({
      id: String(active.id),
      newSequenceOrder: newIndex,
    });
    // Optimistic UI is handled by TanStack Query invalidation after mutate.
    void reordered;
  };

  const handleAddBeat = () => {
    if (!session) return;
    setAddBeatName('');
    setAddBeatDialogOpen(true);
  };

  const handleAddBeatConfirm = async () => {
    if (!session || !addBeatName.trim()) return;
    await createBeat.mutateAsync({
      sessionId: session.id,
      name: addBeatName.trim(),
    });
    setAddBeatDialogOpen(false);
    setAddBeatName('');
  };

  const handleTimelineReorder = (id: string, newIndex: number) => {
    void reorderBeat.mutateAsync({ id, newSequenceOrder: newIndex });
  };

  const handleTimelineSelect = (id: string) => {
    const beat = beats.find((b) => b.id === id) ?? null;
    setSelectedBeat(beat);
  };

  // AI prompt builders
  const suggestNextPrompt = useMemo(() => {
    if (beats.length === 0) return 'Suggest the first beat for a new story.';
    const beatList = beats
      .map(
        (b) =>
          `- ${b.name} (${b.beatType ?? 'beat'}): ${b.description ?? 'No description'}`,
      )
      .join('\n');
    return `Based on the following story beats, suggest the next logical beat in the narrative structure. Provide a title, type (setup/rising_action/climax/etc.), and 2-3 sentence description.\n\nCurrent beats:\n${beatList}`;
  }, [beats]);

  const generateFromSynopsisPrompt = useMemo(() => {
    const synopsis = storySession?.synopsis ?? book?.description ?? '';
    return `Create a complete story structure with 8-12 beats from this synopsis. For each beat provide: title, type, and description.\n\nSynopsis: ${synopsis || '(No synopsis available -- describe your story in the instruction field)'}`;
  }, [storySession, book]);

  if (isLoading) return null;

  if (!session) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2">Story Architect</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            No session yet. Start one to outline the book's beats.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setCreateSessionOpen(true)}
          >
            Start session
          </Button>
        </Stack>
        <CreateSessionDialog
          open={createSessionOpen}
          onClose={() => setCreateSessionOpen(false)}
          structures={structures}
          onCreate={async (structureId) => {
            await createSession.mutateAsync({
              name: book?.title ?? 'Story Session',
              structureId,
            });
            setCreateSessionOpen(false);
          }}
          disabled={createSession.isPending}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: expanded ? 1 : 0 }}
      >
        <Typography variant="subtitle2">Beats ({beats.length})</Typography>
        <Button size="small" onClick={handleAddBeat}>
          + Beat
        </Button>
        <Tooltip title="Suggest next beat (AI)">
          <Button size="small" onClick={() => setSuggestNextOpen(true)}>
            Suggest Next
          </Button>
        </Tooltip>
        <Tooltip title="Generate beats from synopsis (AI)">
          <Button
            size="small"
            onClick={() => setGenerateFromSynopsisOpen(true)}
          >
            From Synopsis
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="List view">
          <IconButton
            size="small"
            color={viewMode === 'list' ? 'primary' : 'default'}
            onClick={() => setViewMode('list')}
          >
            {'\u2630'}
          </IconButton>
        </Tooltip>
        <Tooltip title="Timeline view">
          <IconButton
            size="small"
            color={viewMode === 'timeline' ? 'primary' : 'default'}
            onClick={() => setViewMode('timeline')}
          >
            {'\u25A6'}
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => setExpanded((e) => !e)}>
          {expanded ? '\u2212' : '+'}
        </IconButton>
      </Stack>

      {expanded && viewMode === 'list' && (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ overflowX: 'auto', py: 1 }}
            >
              {beats.map((beat) => (
                <SortableBeatChip
                  key={beat.id}
                  beat={beat}
                  onClick={() => setSelectedBeat(beat)}
                />
              ))}
              {beats.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No beats yet. Use &quot;+ Beat&quot; to outline.
                </Typography>
              )}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      {expanded && viewMode === 'timeline' && (
        <BeatTimeline
          beats={beats}
          selectedBeatId={selectedBeat?.id ?? null}
          onSelectBeat={handleTimelineSelect}
          onReorderBeat={handleTimelineReorder}
        />
      )}

      <BeatDrawer
        bookId={bookId}
        beat={selectedBeat}
        onClose={() => setSelectedBeat(null)}
      />

      {/* AI: Suggest Next Beat modal */}
      <GenerationModal
        open={suggestNextOpen}
        onClose={() => setSuggestNextOpen(false)}
        mode="text"
        bookId={bookId}
        seedPrompt={suggestNextPrompt}
      />

      {/* AI: Generate from Synopsis modal */}
      <GenerationModal
        open={generateFromSynopsisOpen}
        onClose={() => setGenerateFromSynopsisOpen(false)}
        mode="text"
        bookId={bookId}
        seedPrompt={generateFromSynopsisPrompt}
      />

      {/* Add beat dialog */}
      <Dialog
        open={addBeatDialogOpen}
        onClose={() => setAddBeatDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add beat</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Beat name"
            placeholder='e.g. "Inciting incident"'
            value={addBeatName}
            onChange={(e) => setAddBeatName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddBeatConfirm();
            }}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBeatDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddBeatConfirm}
            disabled={!addBeatName.trim() || createBeat.isPending}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function SortableBeatChip({
  beat,
  onClick,
}: {
  beat: BeatDto;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: beat.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{ cursor: 'grab' }}
    >
      <Chip
        label={beat.name}
        color={beat.linkedParagraphIds.length > 0 ? 'primary' : 'default'}
        onClick={onClick}
      />
    </Box>
  );
}

function CreateSessionDialog({
  open,
  onClose,
  structures,
  onCreate,
  disabled,
}: {
  open: boolean;
  onClose: () => void;
  structures: { id: string; name: string }[];
  onCreate: (structureId?: string) => Promise<void>;
  disabled: boolean;
}) {
  const [structureId, setStructureId] = useState<string>('');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Start Story Architect session</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FilterableSelect
            options={structures.map((s) => ({ value: s.id, label: s.name }))}
            value={structureId}
            onChange={(newValue) => setStructureId(newValue)}
            label="Structure"
            allowEmpty={true}
            emptyLabel="None \u2014 freeform beats"
          />
          <FormHelperText>
            You can switch structures later without losing beats.
          </FormHelperText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onCreate(structureId || undefined)}
          disabled={disabled}
        >
          Start
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BeatDrawer({
  bookId,
  beat,
  onClose,
}: {
  bookId: string;
  beat: BeatDto | null;
  onClose: () => void;
}) {
  const update = useUpdateBeat(bookId);
  const deleteBeat = useDeleteBeat(bookId);
  const mapRange = useMapBeatRange(bookId);
  const { data: chapters = [] } = useChapters(bookId);
  const [selectedChapterId, setSelectedChapterId] = useState<string>('');
  const { data: paragraphs = [] } = useParagraphs(
    selectedChapterId || undefined,
  );
  const [pickedParagraphIds, setPickedParagraphIds] = useState<Set<string>>(
    new Set(),
  );
  const [name, setName] = useState(beat?.name ?? '');
  const [description, setDescription] = useState(beat?.description ?? '');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Sync state when a new beat is selected
  if (beat && beat.id !== undefined && beat.name !== name && name === '') {
    setName(beat.name);
    setDescription(beat.description ?? '');
  }

  const togglePicked = (id: string) => {
    setPickedParagraphIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveDetails = async () => {
    if (!beat) return;
    await update.mutateAsync({ id: beat.id, name, description });
  };

  const handleMap = async () => {
    if (!beat || !selectedChapterId) return;
    await mapRange.mutateAsync({
      beatId: beat.id,
      chapterId: selectedChapterId,
      paragraphIds: Array.from(pickedParagraphIds),
    });
    onClose();
  };

  const handleDelete = () => {
    if (!beat) return;
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!beat) return;
    await deleteBeat.mutateAsync(beat.id);
    setDeleteConfirmOpen(false);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={beat !== null}
      onClose={() => {
        setName('');
        setDescription('');
        setSelectedChapterId('');
        setPickedParagraphIds(new Set());
        onClose();
      }}
      PaperProps={{ sx: { width: 480, p: 2 } }}
    >
      {beat && (
        <Stack spacing={2}>
          <Typography variant="h6">Beat</Typography>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            multiline
            minRows={3}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveDetails}
              disabled={update.isPending}
            >
              Save details
            </Button>
            <Button size="small" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ pt: 2 }}>
            Map paragraph range
          </Typography>
          <FilterableSelect
            options={chapters.map((c) => ({ value: c.id, label: c.title }))}
            value={selectedChapterId}
            onChange={(newValue) => setSelectedChapterId(newValue)}
            label="Chapter"
            allowEmpty={true}
            emptyLabel="Select\u2026"
            size="small"
          />

          {selectedChapterId && (
            <>
              <Typography variant="caption" color="text.secondary">
                Click paragraphs to include them in the beat's range.
              </Typography>
              <List
                dense
                sx={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                {paragraphs.map((p) => (
                  <ListItemButton
                    key={p.id}
                    selected={pickedParagraphIds.has(p.id)}
                    onClick={() => togglePicked(p.id)}
                  >
                    <ListItemText
                      primary={stripHtml(p.contentHtml).slice(0, 80) + '…'}
                    />
                  </ListItemButton>
                ))}
              </List>
              <Button
                variant="contained"
                onClick={handleMap}
                disabled={mapRange.isPending || pickedParagraphIds.size === 0}
              >
                Map {pickedParagraphIds.size} paragraph
                {pickedParagraphIds.size === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete beat"
        message={`Delete beat "${beat?.name}"?`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </Drawer>
  );
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}
